import { useLocation, useNavigate } from 'react-router-dom';
import { useStoreTheme } from '../../../store';
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ChevronDownIcon, BuildingOffice2Icon, CheckIcon, ChevronLeftIcon } from "@heroicons/react/24/outline";
import useCompany from '../../../hooks/useCompany';
import useAuth from '../../../hooks/useAuth';

const menu = [
  { name: 'Home', path: '/dashboard/home' },
  { name: 'Admin Users', path: '/dashboard/admin-users' },
  { name: 'API Keys', path: '/dashboard/apikeys' },
  { name: 'Design', path: '/dashboard/design' },
  { name: 'URL Builder', path: '/dashboard/url-builder' },
  { name: 'Configuration', path: '/dashboard/wallet-configuration' },
  { name: 'Members', path: '/dashboard/members' },
  { name: 'Settings', path: '/dashboard/settings' },
  { name: 'Events', path: '/dashboard/events' },
  { name: 'New card', path: '/dashboard/experienceCards/newCard' },
  { name: 'Assets', path: '/dashboard/rwa' },
  { name: 'Invitations', path: '/dashboard/invitations' },
  { name: 'Currencies', path: '/dashboard/admin/currencies' },
  { name: 'Operators', path: '/dashboard/admin/operators' },
  { name: 'Networks', path: '/dashboard/admin/networks' },
  { name: 'Smart Contracts', path: '/dashboard/admin/smart-contracts' },
  { name: 'Methods', path: '/dashboard/admin/methods' },
  { name: 'Checkout', path: '/dashboard/admin/checkout' },
  { name: 'Translations', path: '/dashboard/admin/translations' },
  { name: 'Companies', path: '/dashboard/admin/companies' },
  { name: 'Ramp Sessions', path: '/dashboard/admin/ramp-sessions' },
  { name: 'Integrations', path: '/dashboard/integrations' },
];

export default function Topbar() {
  const { handleNotificationSidebar } = useStoreTheme();
  const location = useLocation();
  const { activeCompany, companies, selectCompany } = useCompany();
  const { user, invitations } = useAuth();
  const navigate = useNavigate();

  const currentSection = menu.find(item => location.pathname.startsWith(item.path))?.name || "Home";

  const getBackTitle = (pathname) => {
    const patterns = [
      { pattern: "/dashboard/rwa/", title: "Assets" },
      { pattern: "/dashboard/admin/companies/", title: "Companies" },
    ];
    return patterns.find(p => pathname.startsWith(p.pattern))?.title || null;
  };
  const backTitle = getBackTitle(location.pathname);

  const pageTitleContent = backTitle ? (
    <div className="flex items-center gap-3 cursor-pointer mt-6" onClick={() => navigate(-1)}>
      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
        <ChevronLeftIcon className="w-4 h-4 text-gray-700" />
      </div>
      <h1 className="text-2xl font-semibold">{backTitle}</h1>
    </div>
  ) : (
    <span className="text-2xl font-semibold text-left mt-6">{currentSection}</span>
  );

  const isDashboardActive = location.pathname.startsWith("/dashboard");
  const isAdminActive = location.pathname.startsWith("/admin");

  const canSeeAdminTab = user?.role === "admin";

  return (
    <div className="py-6 flex items-center justify-between px-8 mx-5">
      <div className="flex min-w-[200px] h-6 items-center">{pageTitleContent}</div>

      <div className="flex items-center gap-4">
        {/* Tabs visual */}
        <div className="flex bg-gray-100 rounded-full p-1 border border-gray-300">
          <button
            onClick={() => navigate("/dashboard/home")}
            className={`px-4 py-2 rounded-full font-semibold text-sm transition-all cursor-pointer ${
              isDashboardActive ? "bg-white text-blue-600 shadow" : "text-gray-700 hover:bg-gray-200"
            }`}
          >
            Company
          </button>

          {canSeeAdminTab && (
            <button
              onClick={() => navigate("/admin/home")}
              className={`px-4 py-2 rounded-full font-semibold text-sm transition-all cursor-pointer ${
                isAdminActive ? "bg-white text-blue-600 shadow" : "text-gray-700 hover:bg-gray-200"
              }`}
            >
              Admin
            </button>
          )}
        </div>

        {/* Selector de compañías */}
        <Menu as="div" className="relative inline-block text-left">
          <MenuButton className="inline-flex max-w-[200px] items-center gap-2 bg-gray-100 py-2 px-4 text-sm font-semibold rounded-full shadow-sm cursor-pointer">
            <span className="truncate block max-w-[140px]">
              {activeCompany?.name || "Select company"}
            </span>
            <ChevronDownIcon className="w-4 h-4 flex-shrink-0" />
          </MenuButton>
          <MenuItems className="absolute right-0 mt-2 w-48 rounded-md bg-white shadow-lg focus:outline-none z-50 overflow-hidden">
            {companies?.map(c => (
              <MenuItem key={c.id} onClick={() => selectCompany(c.id)}>
                <div className={`${activeCompany?.id === c.id ? "bg-gray-100" : ""} group flex w-full items-center justify-between gap-2 px-4 py-2 text-sm text-gray-700`}>
                  <button className="flex items-center gap-2 w-full text-left">
                    <BuildingOffice2Icon className="w-4 h-4 text-gray-500" />
                    {c.name}
                    {activeCompany?.id === c.id && <CheckIcon className="w-4 h-4 text-green-500" />}
                  </button>
                </div>
              </MenuItem>
            ))}
          </MenuItems>
        </Menu>

        {/* Usuario */}
        <div className="flex items-center bg-gray-100 px-4 py-2 rounded-full">
          <img
            src={`https://api.dicebear.com/9.x/initials/svg?backgroundColor=2B36D9&seed=${user?.name || "User"}`}
            alt="User Avatar"
            className="w-6 h-6 rounded-full object-cover mr-2"
          />
          <span className="text-sm font-semibold truncate max-w-[160px]">{user?.name || "User"}</span>
        </div>

        {/* Notificaciones */}
        <button
          className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center cursor-pointer"
          onClick={handleNotificationSidebar}
        >
          <img src="/icons/bell.svg" alt="Notifications" className="w-5 h-5" />
          {invitations?.length > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">
              {invitations?.length}
            </span>
          )}
        </button>
      </div>
    </div>
  );
}